import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../../api";
import { Mic, TrendingUp, Copy, Check } from "lucide-react";

/**
 * Voice-Match Telemetry — distribution of every voice-match check ever run
 * (Sales Pitch submit + every Role Play turn). Bins the 20 slots on cosine
 * similarity [0, 1]. The dashed line marks the currently-live threshold
 * (VOICE_MATCH_THRESHOLD env var); a draggable slider lets L&D preview
 * what would happen if they retuned it — how many currently-passed checks
 * would flip to fail, and how many currently-failed would flip to pass —
 * before they commit the number to the env.
 */
export default function VoiceTelemetry() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(null); // slider value; null = use current_threshold
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/admin/voice-telemetry")
      .then((r) => {
        setD(r.data);
        // iter57 — slider defaults to the SPEAKING threshold since that's
        // what actually gates pitch + roleplay. Operators tuning here are
        // almost always tuning that bar, not the shorter default-kind one.
        setPreview(r.data.thresholds?.speaking ?? r.data.current_threshold);
      })
      .catch((e) => setErr(formatApiError(e)));
  }, []);

  // Recompute pass/fail + flip stats against the slider value. Runs on every
  // pointermove — cheap because `points` is at most 5 000 entries and the
  // reduction is O(n) with tiny constant.
  const stats = useMemo(() => {
    if (!d) return null;
    const t = preview ?? d.current_threshold ?? 0;
    const pts = d.points || [];
    let newPass = 0, newFail = 0, flippedToFail = 0, flippedToPass = 0;
    for (const p of pts) {
      const wouldPass = p.s >= t;
      if (wouldPass) newPass++; else newFail++;
      if (p.p && !wouldPass) flippedToFail++;
      if (!p.p && wouldPass) flippedToPass++;
    }
    return { newPass, newFail, flippedToFail, flippedToPass, t };
  }, [d, preview]);

  const copyEnvLine = async () => {
    if (!stats) return;
    try {
      await navigator.clipboard.writeText(`VOICE_MATCH_THRESHOLD_SPEAKING=${stats.t.toFixed(2)}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* noop */ }
  };

  if (err) return null; // silently hide on error; other integrity signals still render
  if (!d) return null;

  const total = d.total_checks || 0;
  const bins = d.bins || [];
  const maxCount = bins.reduce((m, b) => Math.max(m, (b.passed || 0) + (b.failed || 0)), 0) || 1;
  const liveThreshold = d.current_threshold ?? 0;
  const previewThreshold = stats?.t ?? liveThreshold;
  const dirty = Math.abs(previewThreshold - liveThreshold) > 0.001;

  return (
    <div className="card p-6" data-testid="voice-telemetry">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Mic size={16} className="text-[#EE2737]" />
            <h2 className="font-heading font-bold text-lg">Voice-Match Telemetry</h2>
          </div>
          <p className="text-xs text-[#525252] mt-1 max-w-2xl leading-relaxed">
            Every voice-biometric check ever run — Sales Pitch submit + every Role Play turn.
            Drag the slider to preview a new threshold — bars, totals, and flip counts update live so
            you can settle on a number visually before saving it to the env.
          </p>
        </div>
        <div className="text-right text-xs">
          <div className="uppercase tracking-widest text-[10px] text-[#878787] font-bold">Live thresholds</div>
          {/* iter57 — show BOTH bars the backend actually applies. Pitch +
              Role Play checks are gated by the "speaking" bar (0.25 since
              iter57); shorter default-kind checks use the strict bar. The
              slider below still edits the single-value preview — operators
              set the speaking knob via VOICE_MATCH_THRESHOLD_SPEAKING. */}
          <div className="flex items-end gap-3 justify-end mt-0.5">
            <div>
              <div className="font-heading font-black text-2xl text-[#EE2737]" data-testid="voice-telemetry-threshold-speaking">
                {(d.thresholds?.speaking ?? liveThreshold).toFixed(2)}
              </div>
              <div className="text-[9px] uppercase tracking-widest text-[#878787]">speaking</div>
            </div>
            <div>
              <div className="font-heading font-black text-xl text-[#525252]" data-testid="voice-telemetry-threshold">
                {(d.thresholds?.default ?? liveThreshold).toFixed(2)}
              </div>
              <div className="text-[9px] uppercase tracking-widest text-[#878787]">other</div>
            </div>
          </div>
          <div className="text-[10px] text-[#878787] font-mono mt-1">{d.encoder_version || "—"}</div>
        </div>
      </div>

      {/* Totals — current live counts */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-[#F9FAFB] border border-[#E5E7EB] px-3 py-2.5">
          <div className="text-[10px] uppercase tracking-widest text-[#878787] font-bold">Total checks</div>
          <div className="font-heading font-black text-xl" data-testid="voice-telemetry-total">{total}</div>
        </div>
        <div className="bg-[#D1FAE5] border border-[#10B981] px-3 py-2.5">
          <div className="text-[10px] uppercase tracking-widest text-[#065F46] font-bold">Passed (live)</div>
          <div className="font-heading font-black text-xl text-[#065F46]" data-testid="voice-telemetry-passed">{d.passed || 0}</div>
        </div>
        <div className="bg-[#FEE2E2] border border-[#DC2626] px-3 py-2.5">
          <div className="text-[10px] uppercase tracking-widest text-[#991B1B] font-bold">Failed (live)</div>
          <div className="font-heading font-black text-xl text-[#991B1B]" data-testid="voice-telemetry-failed">{d.failed || 0}</div>
        </div>
      </div>

      {/* Histogram */}
      {total === 0 ? (
        <div className="text-center text-[#878787] py-8 text-sm border border-dashed border-[#E5E7EB]">
          No voice checks recorded yet — telemetry will appear once pilots start Sales Pitch or Role Play.
        </div>
      ) : (
        <div className="space-y-2" data-testid="voice-telemetry-histogram">
          <div className="text-[10px] uppercase tracking-widest text-[#878787] font-bold flex items-center gap-2">
            <TrendingUp size={12} /> Distribution · cosine similarity [0.00 → 1.00]
          </div>
          <div className="relative h-40 flex items-end gap-0.5 border-b border-[#E5E7EB] pt-4">
            {bins.map((b, i) => {
              const passed = b.passed || 0;
              const failed = b.failed || 0;
              const count = passed + failed;
              const heightPct = (count / maxCount) * 100;
              const passedPct = count ? (passed / count) * 100 : 0;
              return (
                <div
                  key={i}
                  className="flex-1 flex flex-col justify-end group relative"
                  style={{ height: "100%" }}
                  data-testid={`voice-telemetry-bin-${i}`}
                >
                  <div
                    className="w-full flex flex-col"
                    style={{ height: `${heightPct}%` }}
                    title={`[${b.lo.toFixed(2)}, ${b.hi.toFixed(2)}) · passed ${passed} · failed ${failed}`}
                  >
                    <div className="bg-[#10B981] w-full" style={{ height: `${passedPct}%` }} />
                    <div className="bg-[#DC2626] w-full flex-1" />
                  </div>
                  {count > 0 && (
                    <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 text-[9px] font-mono text-[#525252]">{count}</div>
                  )}
                </div>
              );
            })}
            {/* Live threshold — faint reference line, always shown */}
            <div
              className="absolute top-0 bottom-0 border-l border-dashed border-[#878787] opacity-60 pointer-events-none"
              style={{ left: `${liveThreshold * 100}%` }}
              data-testid="voice-telemetry-threshold-line"
            >
              <span className="absolute -top-4 left-1 text-[9px] uppercase tracking-widest font-bold bg-[#878787] text-white px-1 whitespace-nowrap">
                live {liveThreshold.toFixed(2)}
              </span>
            </div>
            {/* Preview threshold — bold, follows slider */}
            {dirty && (
              <div
                className="absolute top-0 bottom-0 border-l-2 border-[#EE2737] pointer-events-none transition-[left] duration-75"
                style={{ left: `${previewThreshold * 100}%` }}
                data-testid="voice-telemetry-preview-line"
              >
                <span className="absolute -top-4 left-1 text-[9px] uppercase tracking-widest font-bold bg-[#EE2737] text-white px-1 whitespace-nowrap">
                  preview {previewThreshold.toFixed(2)}
                </span>
              </div>
            )}
          </div>
          <div className="flex justify-between text-[9px] font-mono text-[#878787] pt-1">
            <span>0.00</span>
            <span>0.25</span>
            <span>0.50</span>
            <span>0.75</span>
            <span>1.00</span>
          </div>

          {/* Interactive slider */}
          <div className="pt-4">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-[#878787] font-bold mb-1">
              <span>Preview threshold</span>
              <span className="font-mono text-[#0A0A0A] text-[11px]">{previewThreshold.toFixed(2)}</span>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={previewThreshold}
              onChange={(e) => setPreview(parseFloat(e.target.value))}
              className="w-full accent-[#EE2737] cursor-pointer"
              data-testid="voice-telemetry-slider"
            />
          </div>

          {/* Flip preview */}
          {stats && (
            <div className="grid grid-cols-4 gap-3 pt-4" data-testid="voice-telemetry-flip-stats">
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-widest text-[#878787] font-bold">At preview · pass</div>
                <div className="font-heading font-black text-xl text-[#065F46]" data-testid="voice-telemetry-preview-pass">{stats.newPass}</div>
                <div className="text-[10px] text-[#525252]">
                  {stats.newPass - (d.passed || 0) >= 0 ? "+" : ""}
                  {stats.newPass - (d.passed || 0)} vs live
                </div>
              </div>
              <div className="bg-[#F9FAFB] border border-[#E5E7EB] px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-widest text-[#878787] font-bold">At preview · fail</div>
                <div className="font-heading font-black text-xl text-[#991B1B]" data-testid="voice-telemetry-preview-fail">{stats.newFail}</div>
                <div className="text-[10px] text-[#525252]">
                  {stats.newFail - (d.failed || 0) >= 0 ? "+" : ""}
                  {stats.newFail - (d.failed || 0)} vs live
                </div>
              </div>
              <div className="bg-[#FEF3C7] border border-[#F59E0B] px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-widest text-[#92400E] font-bold">Would flip → fail</div>
                <div className="font-heading font-black text-xl text-[#92400E]" data-testid="voice-telemetry-flip-to-fail">{stats.flippedToFail}</div>
                <div className="text-[10px] text-[#92400E]/80">stricter — was passing</div>
              </div>
              <div className="bg-[#DBEAFE] border border-[#3B82F6] px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-widest text-[#1E3A8A] font-bold">Would flip → pass</div>
                <div className="font-heading font-black text-xl text-[#1E3A8A]" data-testid="voice-telemetry-flip-to-pass">{stats.flippedToPass}</div>
                <div className="text-[10px] text-[#1E3A8A]/80">looser — was failing</div>
              </div>
            </div>
          )}

          {/* Save hint */}
          {dirty && (
            <div className="mt-4 flex items-center justify-between gap-3 bg-[#0A0A0A] text-white px-4 py-3">
              <div className="text-xs">
                <div className="font-bold uppercase tracking-widest text-[10px] text-[#FFC600]">To apply {previewThreshold.toFixed(2)} in production</div>
                <div className="font-mono text-[11px] text-white/85 mt-0.5">
                  set <span className="text-[#FFC600]">VOICE_MATCH_THRESHOLD_SPEAKING={previewThreshold.toFixed(2)}</span> in backend/.env and restart the backend
                </div>
              </div>
              <button
                onClick={copyEnvLine}
                className="btn btn-secondary !text-[10px] !py-1.5 !px-3 flex-shrink-0"
                data-testid="voice-telemetry-copy-env"
              >
                {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy env line</>}
              </button>
            </div>
          )}
          <div className="flex items-center gap-4 text-[10px] text-[#525252] pt-3">
            <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 bg-[#10B981]" /> Passed match</span>
            <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 bg-[#DC2626]" /> Failed match (hold opened)</span>
          </div>
        </div>
      )}

      {/* Recent activity */}
      {(d.recent || []).length > 0 && (
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-widest text-[#878787] font-bold mb-2">Latest 25 checks</div>
          <div className="max-h-56 overflow-y-auto border border-[#E5E7EB]">
            <table className="data-table !text-xs">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Participant</th>
                  <th>Kind</th>
                  <th className="text-right">Similarity</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {d.recent.map((r, i) => (
                  <tr key={i} data-testid={`voice-telemetry-recent-${i}`}>
                    <td className="whitespace-nowrap text-[#525252]">{r.checked_at ? new Date(r.checked_at).toLocaleString() : "—"}</td>
                    <td className="truncate max-w-[180px]">{r.user_name}</td>
                    <td className="font-mono text-[10px] text-[#525252]">{r.kind}</td>
                    <td className="text-right font-mono">{typeof r.similarity === "number" ? r.similarity.toFixed(3) : "—"}</td>
                    <td>
                      {r.passed ? (
                        <span className="badge badge-info !bg-[#D1FAE5] !text-[#065F46]">pass</span>
                      ) : (
                        <span className="badge !bg-[#FEE2E2] !text-[#991B1B]">fail</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
